// src/utils/zip.ts
// ZIP без сжатия (store) в браузере, без зависимостей: для выгрузки снимков —
// JPEG всё равно не сжимается. Файлы до 4 ГБ, всего до 65535 файлов (без ZIP64).
// Имена — UTF-8 (флаг 0x0800), чтобы русские метки читались в Windows и macOS.

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function dosTime(d: Date): [number, number] {
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2);
  const date = ((Math.max(1980, d.getFullYear()) - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return [time, date];
}

export class ZipWriter {
  private parts: BlobPart[] = [];
  private central: Uint8Array[] = [];
  private offset = 0;
  private count = 0;
  private names = new Set<string>();

  add(path: string, data: Uint8Array | string, at: Date = new Date()) {
    // одинаковые имена в архиве: второе получает суффикс
    let name = path;
    for (let k = 1; this.names.has(name); k++) name = path.replace(/(\.[^./]*)?$/, m => `-${k}${m}`);
    this.names.add(name);
    const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    const nameBytes = new TextEncoder().encode(name);
    const crc = crc32(bytes);
    const [time, date] = dosTime(at);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);          // версия для распаковки
    local.setUint16(6, 0x0800, true);      // имена в UTF-8
    local.setUint16(8, 0, true);           // без сжатия
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, bytes.length, true);
    local.setUint32(22, bytes.length, true);
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true);

    const cen = new DataView(new ArrayBuffer(46));
    cen.setUint32(0, 0x02014b50, true);
    cen.setUint16(4, 20, true);
    cen.setUint16(6, 20, true);
    cen.setUint16(8, 0x0800, true);
    cen.setUint16(10, 0, true);
    cen.setUint16(12, time, true);
    cen.setUint16(14, date, true);
    cen.setUint32(16, crc, true);
    cen.setUint32(20, bytes.length, true);
    cen.setUint32(24, bytes.length, true);
    cen.setUint16(28, nameBytes.length, true);
    cen.setUint32(42, this.offset, true);  // смещение локального заголовка

    this.parts.push(new Uint8Array(local.buffer), nameBytes, bytes as BlobPart);
    const c = new Uint8Array(46 + nameBytes.length);
    c.set(new Uint8Array(cen.buffer), 0);
    c.set(nameBytes, 46);
    this.central.push(c);
    this.offset += 30 + nameBytes.length + bytes.length;
    this.count++;
  }

  get size() {
    return this.offset;
  }

  blob(): Blob {
    const cenSize = this.central.reduce((n, c) => n + c.length, 0);
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true);
    end.setUint16(8, this.count, true);
    end.setUint16(10, this.count, true);
    end.setUint32(12, cenSize, true);
    end.setUint32(16, this.offset, true);
    return new Blob([...this.parts, ...(this.central as BlobPart[]), new Uint8Array(end.buffer)], { type: 'application/zip' });
  }
}
